import React, { useMemo } from 'react';
import { Box, Typography, Paper, Grid, CircularProgress, Alert, Divider } from '@mui/material';
import {
  MdAccessTime,
  MdAutorenew,
  MdCalendarToday,
  MdCreditCard,
  MdEvent,
  MdHistory,
  MdInsights,
  MdNotificationsActive,
  MdPayments,
  MdSchedule,
  MdTrendingUp,
} from 'react-icons/md';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { useOutletContext } from 'react-router-dom';
import theme, { tierConfig } from './theme';
import api from '../utils/api';
import { getClientEmail } from '../utils/getClientEmail';
import {
  getPaymentRefundInfo,
  getPaymentStatusLabel,
  getPaymentTypeLabel,
  paymentStatusChipColors,
} from '../utils/paymentRefundDisplay';
import { currentYYYYMM } from './overviewMonthUtils';
import DashboardInsightCard from './DashboardInsightCard';
import SubscriptionPaymentBanner from '../User-app/SubscriptionPaymentBanner';

/** Subscription page palette — aligned with UserDashboard theme + subscription accent */
const sub = {
  accent: theme.colors.chartPurple,
  accentSoft: '#EDE9FE',
  accentBorder: '#c4b5fd',
  accentText: '#3730a3',
  success: theme.colors.accentSuccess,
  muted: theme.colors.textSecondary,
  barTrack: theme.colors.bgTertiary,
  ceiling: theme.colors.accentDanger,
  cardShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
  cardShadowHover: '0 8px 24px rgba(15, 23, 42, 0.08)',
  planCardBorder: '#E5E7EB',
  planCardBg: '#FFFFFF',
  planTitle: theme.colors.textPrimary,
  planDetail: theme.colors.textSecondary,
  planPrice: theme.colors.textPrimary,
  planActiveBorder: '#6366F1',
  planActiveBg: '#F5F3FF',
  planActiveTitle: '#4F46E5',
  planActiveDetail: '#7C73B3',
  planActivePrice: '#4F46E5',
  planComing: '#9CA3AF',
};

const PAGE = {
  maxSectionGap: 3,
  cardRadius: `${theme.radius.lg}px`,
  sectionRadius: `${theme.radius.lg}px`,
};

/** Shared card shell styles */
const cardSx = {
  borderRadius: PAGE.cardRadius,
  bgcolor: theme.colors.bgPrimary,
  border: `1px solid ${theme.colors.borderDefault}`,
  boxShadow: sub.cardShadow,
};

function PageHeader({ title, subtitle }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        component="p"
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: sub.muted,
          mb: 0.75,
        }}
      >
        Billing & usage
      </Typography>
      <Typography
        component="h1"
        sx={{
          fontSize: { xs: 26, md: 30 },
          fontWeight: 800,
          color: theme.colors.textPrimary,
          letterSpacing: '-0.02em',
          lineHeight: 1.15,
          mb: subtitle ? 0.75 : 0,
        }}
      >
        {title}
      </Typography>
      {subtitle ? (
        <Typography sx={{ fontSize: 15, color: sub.muted, fontWeight: 500, maxWidth: 640, lineHeight: 1.5 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
}

function MetricTile({ icon, label, value, subtext, accent = sub.accent }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: `${theme.radius.md}px`,
        bgcolor: theme.colors.bgSecondary,
        border: `1px solid ${theme.colors.borderDefault}`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        transition: 'box-shadow 0.2s, border-color 0.2s',
        '&:hover': { boxShadow: sub.cardShadowHover, borderColor: `${accent}33` },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '10px',
            bgcolor: `${accent}14`,
            color: accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: sub.muted,
            lineHeight: 1.3,
          }}
        >
          {label}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: { xs: 20, md: 22 },
          fontWeight: 700,
          color: theme.colors.textPrimary,
          lineHeight: 1.15,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Typography>
      {subtext ? (
        <Typography sx={{ fontSize: 12, color: sub.muted, fontWeight: 500, lineHeight: 1.4 }}>{subtext}</Typography>
      ) : null}
    </Box>
  );
}

function KpiStatCard({ icon, label, value, subtext, accent = theme.colors.brandPrimary }) {
  return (
    <Paper
      elevation={0}
      sx={{
        ...cardSx,
        p: 2.5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: sub.cardShadowHover },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ color: accent, display: 'flex', flexShrink: 0 }}>{icon}</Box>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: sub.muted,
          }}
        >
          {label}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: 28,
          fontWeight: 700,
          color: theme.colors.textPrimary,
          lineHeight: 1.1,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Typography>
      {subtext ? <Typography sx={{ fontSize: 13, color: sub.muted, fontWeight: 500 }}>{subtext}</Typography> : null}
    </Paper>
  );
}

function UsageStatRow({ label, value, isLast }) {
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 1.35,
          gap: 2,
        }}
      >
        <Typography sx={{ fontSize: 14, color: sub.muted, fontWeight: 500 }}>{label}</Typography>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 700,
            color: theme.colors.textPrimary,
            fontVariantNumeric: 'tabular-nums',
            textAlign: 'right',
          }}
        >
          {value}
        </Typography>
      </Box>
      {!isLast ? <Divider sx={{ borderColor: theme.colors.borderDefault }} /> : null}
    </>
  );
}

function RenewalBanner({ planName, dateLabel, daysAway }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 2.5 },
        mb: 3,
        borderRadius: PAGE.sectionRadius,
        background: `linear-gradient(135deg, ${sub.accent} 0%, #6366f1 100%)`,
        border: '1px solid rgba(255,255,255,0.12)',
        boxShadow: '0 8px 24px rgba(80, 70, 179, 0.28)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '12px',
          bgcolor: 'rgba(255,255,255,0.16)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <MdNotificationsActive size={24} color="#fff" />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', color: 'rgba(255,255,255,0.78)', mb: 0.5 }}>
          RENEWAL REMINDER
        </Typography>
        <Typography sx={{ fontSize: 16, color: '#fff', fontWeight: 600, lineHeight: 1.45 }}>
          Your {planName} renews on {dateLabel}
          {daysAway != null && daysAway >= 0 ? (
            <Box component="span" sx={{ opacity: 0.88, fontWeight: 500 }}>
              {' '}
              · {daysAway} day{daysAway === 1 ? '' : 's'} away
            </Box>
          ) : null}
        </Typography>
      </Box>
    </Paper>
  );
}

function UsageChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <Paper elevation={2} sx={{ p: 1.5, borderRadius: `${theme.radius.md}px`, border: `1px solid ${theme.colors.borderDefault}` }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: theme.colors.textPrimary, mb: 0.25 }}>{row.month}</Typography>
      <Typography sx={{ fontSize: 12, color: sub.muted }}>{row.hours?.toFixed?.(1) ?? row.hours ?? 0} hours</Typography>
    </Paper>
  );
}

const GOLD_TIER_META = { id: 'Gold', hours: 30, perHourINR: 400, monthlyINR: 12000, coming: false };

function parseTierFromPlanName(planName) {
  if (!planName || typeof planName !== 'string') return null;
  const base = planName.replace(/-plan$/i, '').replace(/_/g, ' ').trim();
  if (!base) return null;
  return base.charAt(0).toUpperCase() + base.slice(1).toLowerCase();
}

function pickLatestPayment(paymentHistory) {
  if (!Array.isArray(paymentHistory) || !paymentHistory.length) return null;
  return [...paymentHistory].sort((a, b) => {
    const da = new Date(a.paymentDate || a.createdAt || 0).getTime();
    const db = new Date(b.paymentDate || b.createdAt || 0).getTime();
    return db - da;
  })[0];
}

function formatINR(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(n));
}

function formatDateShort(iso) {
  if (!iso || iso === '—') return '—';
  const parsed = parseRenewalDateFlexible(iso);
  if (parsed) return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Parse billing / renewal values from APIs (ISO, DD/MM/YYYY, DD-MM-YYYY, YYYY-MM-DD, Unix s/ms).
 * Prefer this over `new Date(string)` alone — many backends send non-ISO dates.
 */
function parseRenewalDateFlexible(raw) {
  if (raw == null || raw === '') return null;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const ms = raw < 1e12 ? raw * 1000 : raw;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const s0 = String(raw).trim();
  if (!s0 || s0 === '—') return null;

  const dIsoTry = new Date(s0);
  if (!Number.isNaN(dIsoTry.getTime())) return dIsoTry;

  // DD/MM/YYYY or DD-MM-YYYY (day first — common in IN APIs)
  const m1 = s0.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:\s|$)/);
  if (m1) {
    const day = Number(m1[1]);
    const month = Number(m1[2]) - 1;
    const year = Number(m1[3]);
    const d = new Date(year, month, day);
    if (!Number.isNaN(d.getTime()) && d.getFullYear() === year && d.getMonth() === month && d.getDate() === day) return d;
  }

  const m2 = s0.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m2) {
    const y = Number(m2[1]);
    const mo = Number(m2[2]) - 1;
    const day = Number(m2[3]);
    const d = new Date(y, mo, day);
    if (!Number.isNaN(d.getTime())) return d;
  }

  // Compact YYYYMMDD
  const m3 = s0.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (m3) {
    const y = Number(m3[1]);
    const mo = Number(m3[2]) - 1;
    const day = Number(m3[3]);
    const d = new Date(y, mo, day);
    if (!Number.isNaN(d.getTime())) return d;
  }

  return null;
}

/** Long month name for renewal banner (e.g. 15 April 2026). */
function formatRenewalBannerDate(isoOrDate) {
  if (!isoOrDate || isoOrDate === '—') return '—';
  const parsed =
    isoOrDate instanceof Date && !Number.isNaN(isoOrDate.getTime())
      ? isoOrDate
      : parseRenewalDateFlexible(isoOrDate);
  if (parsed) return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return String(isoOrDate).trim() || '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function isRenewalDateParsable(s) {
  return parseRenewalDateFlexible(s) != null;
}

/** Banner shows in the last N days before renewal / next subscription (always visible in-window; not dismissible). */
const RENEWAL_REMINDER_WINDOW_DAYS = 120;

/** Single line like "Diamond plan" for the renewal reminder (avoids "plan plan"). */
function buildRenewalBannerPlanPhrase(displayTier, latest, plan, usePlanUsage, pu) {
  const candidates = [usePlanUsage && pu?.planLabel, plan?.planName, plan?.name, latest?.planName].filter(
    (s) => typeof s === 'string' && String(s).trim(),
  );
  const raw = candidates[0] != null ? String(candidates[0]).trim() : '';
  if (raw) {
    let s = raw.replace(/-plan$/i, '').replace(/_/g, ' ').trim();
    const lower = s.toLowerCase();
    if (lower.endsWith(' plan')) return s;
    if (lower.endsWith(' tier')) s = s.replace(/\s+tier$/i, '').trim();
    return `${s} plan`;
  }
  const tier = (displayTier || 'Silver').toString().trim();
  return `${tier} plan`;
}

function daysFromNowTo(iso) {
  if (iso == null || iso === '') return null;
  const end = parseRenewalDateFlexible(iso);
  if (!end) {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    const endNaive = new Date(t);
    endNaive.setHours(0, 0, 0, 0);
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return Math.ceil((endNaive - start) / 86400000);
  }
  end.setHours(0, 0, 0, 0);
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  return Math.ceil((end - start) / 86400000);
}

/** First finite number in argument list (skips null / undefined / NaN). */
function firstFiniteNumber(...candidates) {
  for (const v of candidates) {
    if (v === '' || v === null || v === undefined) continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** Trim API date-ish values (null, empty, em dash). */
function trimRenewalCandidate(v) {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s || s === '—') return null;
  return s;
}

const NEXT_BILLING_FIELD_KEYS = [
  'nextSubscriptionDateISO',
  'next_subscription_date_iso',
  'nextSubscriptionDate',
  'next_subscription_date',
  'nextBilling',
  'next_billing_date',
  'nextBillingDate',
  'nextPaymentDate',
  'next_payment_date',
  'renewalDate',
  'trialEndsAt',
  'trial_ends_at',
  'trialEndDate',
  'trial_end_date',
  'trialEnd',
  'subscriptionEndDate',
  'subscription_end_date',
  'subscriptionEndsAt',
  'subscription_end',
  'currentPeriodEnd',
  'current_period_end',
  'billingPeriodEnd',
  'billing_period_end',
  'periodEnd',
  'period_end',
  'expiresAt',
  'expireAt',
  'endDate',
  'end_date',
  'validUntil',
  'valid_until',
];

/** Next charge / renewal / trial end on one plan-usage row or nested plan blob. */
function extractNextBillingFromObjectDeep(o) {
  if (!o || typeof o !== 'object') return null;
  for (const k of NEXT_BILLING_FIELD_KEYS) {
    const t = trimRenewalCandidate(o[k]);
    if (t) return t;
  }
  const nested = o.plan || o.activePlan || o.subscription || o.currentPlan || o.planDetails || {};
  if (nested && typeof nested === 'object' && nested !== o) {
    for (const k of NEXT_BILLING_FIELD_KEYS) {
      const t = trimRenewalCandidate(nested[k]);
      if (t) return t;
    }
  }
  return null;
}

/** Walk `/user/plan-usage` body (object or array) for any billing date. */
function extractBillingFromPlanUsageState(planUsage) {
  if (planUsage == null) return null;
  if (Array.isArray(planUsage)) {
    for (const row of planUsage) {
      const x = extractNextBillingFromObjectDeep(row);
      if (x) return x;
    }
    return null;
  }
  return extractNextBillingFromObjectDeep(planUsage);
}

function earliestPaymentMonthLabel(paymentHistory) {
  if (!Array.isArray(paymentHistory) || !paymentHistory.length) return null;
  let best = Infinity;
  for (const p of paymentHistory) {
    const t = new Date(p.billing_start || p.paymentDate || p.createdAt || 0).getTime();
    if (!Number.isNaN(t) && t < best) best = t;
  }
  if (!Number.isFinite(best)) return null;
  return new Date(best).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

/** Chart `month` / `label` matches refDate’s calendar month (handles Apr, April, Apr 2026). */
function chartMonthLabelMatchesRefDate(monthLabel, refDate = new Date()) {
  if (monthLabel == null) return false;
  const s = String(monthLabel).trim().toLowerCase();
  if (!s || s === '—') return false;
  const shortM = refDate.toLocaleDateString('en-GB', { month: 'short' }).toLowerCase();
  const longM = refDate.toLocaleDateString('en-GB', { month: 'long' }).toLowerCase();
  const y = String(refDate.getFullYear());
  if (s === shortM || s === longM) return true;
  if (s.startsWith(shortM) && (s.length === shortM.length || !/[a-z]/.test(s.charAt(shortM.length)))) return true;
  if (s.includes(shortM) && (s.includes(y) || s.length <= shortM.length + 2)) return true;
  if (s.includes(longM.slice(0, 3)) && s.includes(y)) return true;
  return false;
}

function sumHoursForCalendarMonth(rows, refDate = new Date()) {
  if (!Array.isArray(rows) || !rows.length) return null;
  let sum = 0;
  let hit = false;
  for (const r of rows) {
    const label = r?.month ?? r?.label ?? r?.period;
    if (!chartMonthLabelMatchesRefDate(label, refDate)) continue;
    hit = true;
    sum += Number(r.hours ?? r.h ?? r.Hours ?? 0) || 0;
  }
  return hit ? sum : null;
}

function sumSessionsForCalendarMonth(rows, refDate = new Date()) {
  if (!Array.isArray(rows) || !rows.length) return null;
  let sum = 0;
  let hit = false;
  for (const r of rows) {
    const label = r?.month ?? r?.label ?? r?.period;
    if (!chartMonthLabelMatchesRefDate(label, refDate)) continue;
    hit = true;
    sum +=
      Number(
        r.sessions ??
          r.sessionCount ??
          r.session_count ??
          r.count ??
          r.totalSessions ??
          r.completedSessions ??
          r.s ??
          0,
      ) || 0;
  }
  return hit ? sum : null;
}

function tierHours(tier) {
  if (!tier) return 36;
  const t = tierConfig[tier]?.hours;
  if (t != null) return t;
  if (tier.toLowerCase() === 'gold') return GOLD_TIER_META.hours;
  return 36;
}

/** Combine multiple `/user/plan-usage` rows (e.g. per robot) into one card. */
function aggregatePlanUsageRows(rows) {
  if (!Array.isArray(rows) || !rows.length) return null;
  const usedSeconds = rows.reduce((s, r) => s + (Number(r.usedSeconds) || 0), 0);
  const remainingSeconds = rows.reduce((s, r) => s + (Number(r.remainingSeconds) || 0), 0);
  const totalHoursPerMonth = rows.reduce((s, r) => s + (Number(r.totalHoursPerMonth) || 0), 0);
  const usedH = usedSeconds / 3600;
  const totalCombined = usedSeconds + remainingSeconds;
  const usagePct =
    totalCombined > 0
      ? Math.min(100, (usedSeconds / totalCombined) * 100)
      : totalHoursPerMonth > 0
        ? Math.min(100, (usedH / totalHoursPerMonth) * 100)
        : null;
  let nextBillingMerged = null;
  for (const r of rows) {
    nextBillingMerged = extractNextBillingFromObjectDeep(r);
    if (nextBillingMerged) break;
  }

  let pools = null;
  if (rows.some((r) => r.pools)) {
    pools = {
      main: { quotaSeconds: 0, usedSeconds: 0, remainingSeconds: 0, status: 'inactive' },
      addon: { purchasedSeconds: 0, usedSeconds: 0, remainingSeconds: 0, status: 'inactive' },
      admin: { grantedSeconds: 0, usedSeconds: 0, remainingSeconds: 0, status: 'inactive' },
      totalUsedSeconds: 0,
      totalRemainingSeconds: 0,
    };
    for (const r of rows) {
      if (!r.pools) continue;
      pools.main.quotaSeconds += Number(r.pools.main?.quotaSeconds) || 0;
      pools.main.usedSeconds += Number(r.pools.main?.usedSeconds) || 0;
      pools.main.remainingSeconds += Number(r.pools.main?.remainingSeconds) || 0;
      pools.addon.purchasedSeconds += Number(r.pools.addon?.purchasedSeconds) || 0;
      pools.addon.usedSeconds += Number(r.pools.addon?.usedSeconds) || 0;
      pools.addon.remainingSeconds += Number(r.pools.addon?.remainingSeconds) || 0;
      pools.admin.grantedSeconds += Number(r.pools.admin?.grantedSeconds) || 0;
      pools.admin.usedSeconds += Number(r.pools.admin?.usedSeconds) || 0;
      pools.admin.remainingSeconds += Number(r.pools.admin?.remainingSeconds) || 0;
      pools.totalUsedSeconds += Number(r.pools.totalUsedSeconds) || 0;
      pools.totalRemainingSeconds += Number(r.pools.totalRemainingSeconds) || 0;
      if (r.pools.main?.status === 'active') pools.main.status = 'active';
      if (r.pools.addon?.status === 'active') pools.addon.status = 'active';
      if (r.pools.admin?.status === 'active') pools.admin.status = 'active';
    }
  }

  const firstWithPools = rows.find((r) => r.pools || r.poolStatuses);
  return {
    user: rows[0]?.user,
    robotId: rows.map((r) => r.robotId).filter(Boolean).join(', '),
    totalHoursPerMonth: totalHoursPerMonth || null,
    usedSeconds,
    remainingSeconds,
    usagePercentage: usagePct != null ? String(usagePct.toFixed(2)) : null,
    usedTimeFormatted: rows.map((r) => r.usedTimeFormatted).find(Boolean) || null,
    remainingTimeFormatted: rows.map((r) => r.remainingTimeFormatted).find(Boolean) || null,
    planpaymentStatus: rows.map((r) => r.planpaymentStatus).find(Boolean) || null,
    nextBilling: nextBillingMerged,
    pools,
    poolStatuses: firstWithPools?.poolStatuses || null,
    adminGrantedSeconds: rows.reduce((s, r) => s + (Number(r.adminGrantedSeconds) || 0), 0) || null,
    adminRemainingSeconds: rows.reduce((s, r) => s + (Number(r.adminRemainingSeconds) || 0), 0) || null,
  };
}

/** Unwrap `/user/plan-usage` response (`data` may be an object or an array of usage rows). */
function normalizePlanUsageBody(res) {
  const d = res?.data;
  if (!d || typeof d !== 'object') return null;
  if (d.success && d.data != null) {
    if (Array.isArray(d.data)) {
      if (d.data.length === 0) return null;
      if (d.data.length === 1) return d.data[0];
      return aggregatePlanUsageRows(d.data);
    }
    if (typeof d.data === 'object') return d.data;
  }
  if (d.success) {
    const { success: _s, message: _m, error: _e, ...rest } = d;
    if (Object.keys(rest).length > 0) return rest;
  }
  return d;
}

/** Map flexible API field names onto one shape for the Current plan card. */
function pickPlanUsage(pu) {
  if (!pu || typeof pu !== 'object') return null;
  const nested = pu.plan || pu.activePlan || pu.subscription || pu.currentPlan || {};

  const usedFromSeconds =
    pu.usedSeconds != null && pu.usedSeconds !== '' && Number.isFinite(Number(pu.usedSeconds))
      ? Number(pu.usedSeconds) / 3600
      : null;
  const remainingFromSeconds =
    pu.remainingSeconds != null && pu.remainingSeconds !== '' && Number.isFinite(Number(pu.remainingSeconds))
      ? Number(pu.remainingSeconds) / 3600
      : null;

  const includedHours = firstFiniteNumber(
    pu.includedHours,
    pu.included_hours,
    pu.hoursIncluded,
    pu.hours_included,
    pu.totalHours,
    pu.total_hours,
    pu.totalHoursPerMonth,
    pu.total_hours_per_month,
    pu.planHours,
    pu.plan_hours,
    pu.tierHours,
    pu.allocHours,
    pu.allocatedHours,
    nested.includedHours,
    nested.hoursIncluded,
    nested.totalHours,
    nested.hours,
    nested.totalHoursPerMonth,
  );

  const usedHours = firstFiniteNumber(
    pu.usedHours,
    pu.used_hours,
    pu.hoursUsed,
    pu.hours_used,
    pu.consumedHours,
    pu.consumed_hours,
    nested.usedHours,
    nested.hoursUsed,
    usedFromSeconds,
  );

  const remainingHours = firstFiniteNumber(
    pu.remainingHours,
    pu.remaining_hours,
    pu.hoursRemaining,
    pu.hours_remaining,
    nested.remainingHours,
    remainingFromSeconds,
  );

  const monthlyFee = firstFiniteNumber(
    pu.monthlyFee,
    pu.monthly_fee,
    pu.price,
    pu.amount,
    pu.finalPrice,
    nested.monthlyFee,
    nested.price,
  );
  const nextBilling = extractNextBillingFromObjectDeep(pu) || extractNextBillingFromObjectDeep(nested);
  const usagePct = firstFiniteNumber(pu.usagePct, pu.usage_pct, pu.usagePercentage, pu.usage_percentage);

  const usedTimeFormatted =
    typeof pu.usedTimeFormatted === 'string' && pu.usedTimeFormatted.trim() ? pu.usedTimeFormatted.trim() : null;
  const remainingTimeFormatted =
    typeof pu.remainingTimeFormatted === 'string' && pu.remainingTimeFormatted.trim() ? pu.remainingTimeFormatted.trim() : null;

  const planpaymentStatus =
    typeof pu.planpaymentStatus === 'string' && pu.planpaymentStatus.trim() ? pu.planpaymentStatus.trim() : null;

  const planLabel =
    (typeof pu.planName === 'string' && pu.planName.trim()) ||
    (typeof pu.plan_name === 'string' && pu.plan_name.trim()) ||
    (typeof pu.name === 'string' && pu.name.trim() && pu.name !== pu.user) ||
    (typeof pu.label === 'string' && pu.label.trim()) ||
    (typeof nested.name === 'string' && nested.name.trim()) ||
    (typeof nested.planName === 'string' && nested.planName.trim()) ||
    null;

  const pools = pu.pools && typeof pu.pools === 'object' ? pu.pools : null;
  const poolStatuses = pu.poolStatuses && typeof pu.poolStatuses === 'object' ? pu.poolStatuses : null;
  const adminGrantedSeconds = firstFiniteNumber(pu.adminGrantedSeconds, pools?.admin?.grantedSeconds);
  const adminRemainingSeconds = firstFiniteNumber(pu.adminRemainingSeconds, pools?.admin?.remainingSeconds);
  const totalCombinedQuotaSeconds = firstFiniteNumber(
    pu.totalCombinedQuotaSeconds,
    pools?.totalUsedSeconds != null && pools?.totalRemainingSeconds != null
      ? Number(pools.totalUsedSeconds) + Number(pools.totalRemainingSeconds)
      : null,
  );

  const hasPlanUsageRow =
    includedHours != null ||
    usedHours != null ||
    remainingHours != null ||
    usedFromSeconds != null ||
    remainingFromSeconds != null ||
    firstFiniteNumber(pu.totalHoursPerMonth) != null ||
    planLabel != null ||
    monthlyFee != null ||
    nextBilling != null ||
    usagePct != null ||
    pools != null ||
    adminGrantedSeconds != null;

  if (!hasPlanUsageRow) return null;

  return {
    planLabel,
    includedHours,
    usedHours,
    remainingHours,
    usedTimeFormatted,
    remainingTimeFormatted,
    monthlyFee,
    nextBilling,
    usagePct,
    planpaymentStatus,
    pools,
    poolStatuses,
    adminGrantedSeconds,
    adminRemainingSeconds,
    totalCombinedQuotaSeconds,
    usedFromMainSeconds: firstFiniteNumber(pu.usedFromMainSeconds),
    usedFromAddonSeconds: firstFiniteNumber(pu.usedFromAddonSeconds),
    usedFromAdminSeconds: firstFiniteNumber(pu.usedFromAdminSeconds),
  };
}

/** Semicircle usage gauge (0–100 %) */
function SemicircleGauge({ pct }) {
  const p = Math.max(0, Math.min(100, pct));
  const r = 88;
  const cx = 100;
  const cy = 100;
  const arcLen = Math.PI * r;
  const dash = (p / 100) * arcLen;
  return (
    <Box
      sx={{
        width: 220,
        mx: 'auto',
        position: 'relative',
        height: 120,
        py: 1,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: '8px 10px 0',
          borderRadius: '50%',
          bgcolor: theme.colors.bgSecondary,
          opacity: 0.6,
        }}
      />
      <svg width="220" height="118" viewBox="0 0 200 120" aria-hidden style={{ position: 'relative', zIndex: 1 }}>
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none"
          stroke={sub.barTrack}
          strokeWidth={14}
          strokeLinecap="round"
        />
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none"
          stroke={sub.accent}
          strokeWidth={14}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${arcLen}`}
          style={{ transition: 'stroke-dasharray 0.6s ease' }}
        />
      </svg>
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 12,
          textAlign: 'center',
          pointerEvents: 'none',
          zIndex: 2,
        }}
      >
        <Typography sx={{ fontSize: 32, fontWeight: 800, color: sub.accentText, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
          {p}%
        </Typography>
        <Typography sx={{ fontSize: 12, color: sub.muted, fontWeight: 600, mt: 0.25 }}>used this cycle</Typography>
      </Box>
    </Box>
  );
}

/**
 * Unwraps AI/cache payloads like Mongo `surface: "subscription"` docs or `{ success, data }` envelopes.
 */
function normalizeUsageForecastInsight(fromRes) {
  const raw = fromRes && typeof fromRes === 'object' && 'data' in fromRes ? fromRes.data : fromRes;
  if (raw == null || typeof raw !== 'object') return null;
  const payload = raw.success !== undefined && raw.data !== undefined ? raw.data : raw;
  if (!payload || typeof payload !== 'object') return null;
  const narrative = typeof payload.narrative === 'string' ? payload.narrative.trim() : '';
  const points = Array.isArray(payload.points) ? payload.points : [];
  const error = typeof payload.error === 'string' ? payload.error.trim() : '';
  if (!error && !narrative && points.length === 0) return null;
  return {
    narrative,
    points,
    error,
    fromCache: payload.fromCache ?? false,
    generatedAt: payload.generatedAt,
    cacheMode: payload.cacheMode,
    source: payload.source,
  };
}

/** seconds -> hours (number), null-safe. */
function secToHours(sec) {
  if (sec == null || sec === '' || !Number.isFinite(Number(sec))) return 0;
  return Number(sec) / 3600;
}

/** "12.5 h" formatting for entitlement / usage rows. */
function formatHoursLabel(h) {
  if (h == null || !Number.isFinite(Number(h))) return '—';
  return `${(Math.round(Number(h) * 10) / 10).toFixed(1)} h`;
}

/** Payment / subscription status -> chip colors + label. */
const STATUS_CHIP = {
  captured: { bg: '#ecfdf5', color: '#047857', label: 'Captured' },
  active: { bg: '#ecfdf5', color: '#047857', label: 'Active' },
  authenticated: { bg: '#eff6ff', color: '#1d4ed8', label: 'Authenticated' },
  authorized: { bg: '#eff6ff', color: '#1d4ed8', label: 'Authorized' },
  trial: { bg: '#f5f3ff', color: '#6d28d9', label: 'Trial' },
  pending: { bg: '#fffbeb', color: '#b45309', label: 'Pending' },
  created: { bg: '#f8fafc', color: '#475569', label: 'Created' },
  refunded: { bg: '#fef2f2', color: '#b91c1c', label: 'Refunded' },
  failed: { bg: '#fef2f2', color: '#b91c1c', label: 'Failed' },
  expired: { bg: '#f1f5f9', color: '#64748b', label: 'Expired' },
};

function statusChipStyle(status) {
  const key = String(status || '').trim().toLowerCase();
  return (
    STATUS_CHIP[key] || {
      bg: '#f1f5f9',
      color: '#475569',
      label: status ? String(status) : '—',
    }
  );
}

/** Small pill component for statuses. */
function StatusChip({ status, size = 'sm' }) {
  const s = statusChipStyle(status);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        px: size === 'sm' ? 1 : 1.25,
        py: size === 'sm' ? 0.25 : 0.5,
        borderRadius: 999,
        bgcolor: s.bg,
        color: s.color,
        fontSize: size === 'sm' ? 11 : 12,
        fontWeight: 700,
        letterSpacing: '0.02em',
        textTransform: 'capitalize',
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
      }}
    >
      {s.label}
    </Box>
  );
}

/** Payment row status chip (refund-aware colors from paymentRefundDisplay). */
function PaymentStatusChip({ payment, size = 'sm' }) {
  const colors = paymentStatusChipColors(payment);
  const label = getPaymentStatusLabel(payment);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        px: size === 'sm' ? 1 : 1.25,
        py: size === 'sm' ? 0.25 : 0.5,
        borderRadius: 999,
        bgcolor: colors.bg,
        color: colors.color,
        fontSize: size === 'sm' ? 11 : 12,
        fontWeight: 700,
        letterSpacing: '0.02em',
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
}

function formatPaymentMethodLabel(raw) {
  if (raw == null || raw === '') return null;
  const key = String(raw).trim().toLowerCase().replace(/\s+/g, '_');
  const labels = {
    card: 'Card',
    credit: 'Credit card',
    credit_card: 'Credit card',
    debit: 'Debit card',
    debit_card: 'Debit card',
    upi: 'UPI',
    netbanking: 'Net banking',
    net_banking: 'Net banking',
    wallet: 'Wallet',
    emi: 'EMI',
    autopay: 'Autopay',
    mandate: 'Autopay mandate',
  };
  return labels[key] || String(raw).trim().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function normalizePurchaseTypeLabel(raw) {
  const norm = String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
  if (norm === 'subscriptionuser') return 'Subscription';
  if (norm === 'purchaseuser') return 'Purchase';
  if (!norm) return null;
  return norm.charAt(0).toUpperCase() + norm.slice(1);
}

function pickPaymentMethodFromRow(p) {
  if (!p || typeof p !== 'object') return null;
  const entity = p.entity && typeof p.entity === 'object' ? p.entity : null;
  const candidates = [
    p.payment_method,
    p.paymentMethod,
    p.method,
    p.razorpayDetails?.method,
    entity?.method,
    entity?.payment_method,
    entity?.paymentMethod,
    entity?.channel,
    entity?.card?.network,
    entity?.card?.issuer,
    entity?.card?.type,
    entity?.vpa ? 'upi' : null,
  ];
  for (const c of candidates) {
    if (c != null && String(c).trim()) return String(c).trim();
  }
  return null;
}

/** Walk payment history (captured first) for the first row with a method. */
function pickPaymentMethodFromRows(paymentRows) {
  if (!Array.isArray(paymentRows) || !paymentRows.length) return null;
  const statusRank = (status) => {
    const st = String(status || '').toLowerCase();
    if (st === 'captured') return 0;
    if (st === 'authenticated' || st === 'authorized') return 1;
    if (st === 'refunded') return 2;
    return 3;
  };
  const sorted = [...paymentRows].sort((a, b) => {
    const rank = statusRank(a.status) - statusRank(b.status);
    if (rank !== 0) return rank;
    return (
      new Date(b.paymentDate || b.createdAt || 0).getTime() -
      new Date(a.paymentDate || a.createdAt || 0).getTime()
    );
  });
  for (const row of sorted) {
    const method = pickPaymentMethodFromRow(row);
    if (method) return method;
  }
  return null;
}

/** Primary payment method / autopay label for the plan hero. */
function resolveSubscriptionPaymentDisplay(usd, paymentRows, activeRich, billing) {
  const methodRaw =
    pickPaymentMethodFromRows(paymentRows) ||
    activeRich?.paymentMethod ||
    activeRich?.payment_method ||
    usd?.paymentMethod ||
    usd?.payment_method ||
    billing?.paymentMethod ||
    billing?.payment_method ||
    null;

  const latestCaptured =
    paymentRows.find((p) =>
      ['captured', 'authenticated', 'authorized'].includes(String(p.status || '').toLowerCase()),
    ) ||
    paymentRows[0] ||
    null;

  const method = methodRaw ? formatPaymentMethodLabel(methodRaw) : null;
  const purchaseType = normalizePurchaseTypeLabel(usd?.purchaseType);
  const isAutopay = Boolean(
    usd?.razorpay_subscription_id ||
      usd?.razorpay_mandate_id ||
      activeRich?.razorpay_subscription_id ||
      latestCaptured?.razorpay_subscription_id ||
      latestCaptured?.mandateId ||
      usd?.mandateId ||
      (activeRich?.autoPayMonths != null && Number(activeRich.autoPayMonths) > 0),
  );
  const transactionType = getPaymentTypeLabel(latestCaptured?.type);

  const primaryParts = [];
  if (method) primaryParts.push(method);
  if (isAutopay) primaryParts.push('Autopay');
  else if (transactionType && !method) primaryParts.push(transactionType);

  let label = primaryParts.length > 0 ? primaryParts.join(' · ') : null;
  if (!label && purchaseType) label = purchaseType;
  if (!label && isAutopay) label = 'Autopay';
  if (!label) label = '—';

  const subParts = [];
  if (purchaseType) subParts.push(`${purchaseType} account`);
  if (isAutopay && method) subParts.push('Recurring mandate');
  else if (isAutopay) subParts.push('Razorpay recurring');
  else if (transactionType && method) subParts.push(transactionType);

  return {
    label,
    method,
    purchaseType,
    isAutopay,
    transactionType,
    subtext: subParts.length ? subParts.join(' · ') : method ? 'Payment method on file' : 'No payment method recorded yet',
  };
}

function poolStatusMeta(status) {
  switch (String(status || '').toLowerCase()) {
    case 'active':
      return { label: 'Active', color: '#047857', bg: '#ecfdf5' };
    case 'depleted':
      return { label: 'Depleted', color: '#b45309', bg: '#fffbeb' };
    default:
      return { label: 'Inactive', color: '#64748b', bg: '#f1f5f9' };
  }
}

function PoolCard({ title, subtitle, remainingSeconds, quotaSeconds, usedSeconds, status, accentColor, large = false }) {
  const meta = poolStatusMeta(status);
  return (
    <Paper
      elevation={0}
      sx={{
        p: large ? 3 : 2.25,
        borderRadius: `${theme.radius.md}px`,
        border: `1px solid ${theme.colors.borderDefault}`,
        bgcolor: theme.colors.bgSecondary,
        height: '100%',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: sub.cardShadowHover },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, mb: large ? 2 : 1.75 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: large ? 16 : 14, fontWeight: 700, color: theme.colors.textPrimary }}>{title}</Typography>
          <Typography sx={{ fontSize: large ? 13 : 12, color: sub.muted, mt: 0.35 }}>{subtitle}</Typography>
        </Box>
        <Box
          component="span"
          sx={{
            px: 1.25,
            py: 0.4,
            borderRadius: 999,
            bgcolor: meta.bg,
            color: meta.color,
            fontSize: large ? 11 : 10,
            fontWeight: 700,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          {meta.label}
        </Box>
      </Box>
      <Typography
        sx={{
          fontSize: large ? 32 : 24,
          fontWeight: 800,
          color: accentColor,
          lineHeight: 1.15,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatHoursLabel(secToHours(remainingSeconds))}
      </Typography>
      <Typography sx={{ fontSize: large ? 13 : 12, color: sub.muted, mt: 0.5, fontWeight: 500 }}>remaining</Typography>
      <Divider sx={{ my: large ? 2 : 1.5, borderColor: theme.colors.borderDefault }} />
      <Typography sx={{ fontSize: large ? 13 : 12, color: sub.muted, lineHeight: 1.55 }}>
        Quota{' '}
        <Box component="span" sx={{ color: theme.colors.textPrimary, fontWeight: 700 }}>
          {formatHoursLabel(secToHours(quotaSeconds))}
        </Box>
        {' · '}
        Used{' '}
        <Box component="span" sx={{ color: theme.colors.textPrimary, fontWeight: 700 }}>
          {formatHoursLabel(secToHours(usedSeconds))}
        </Box>
      </Typography>
    </Paper>
  );
}

/** Three-pool breakdown: main / add-on / admin bonus (matches /user/plan-usage). */
function hasVisibleTimePools(timePools) {
  if (!timePools) return false;
  const { main, addon, admin } = timePools;
  return (
    (main?.quotaSeconds || 0) > 0 ||
    (addon?.purchasedSeconds || addon?.quotaSeconds || 0) > 0 ||
    (admin?.grantedSeconds || 0) > 0
  );
}

function TimePoolsSection({ timePools, adminTimeGrants, large = false }) {
  if (!hasVisibleTimePools(timePools)) return null;
  const { main, addon, admin } = timePools;
  const grants = Array.isArray(adminTimeGrants) ? adminTimeGrants : [];

  return (
    <Box>
      <Grid container spacing={large ? 2.5 : 2}>
        <Grid item xs={12} sm={4}>
          <PoolCard
            large={large}
            title="Main plan"
            subtitle="Resets monthly"
            remainingSeconds={main?.remainingSeconds}
            quotaSeconds={main?.quotaSeconds}
            usedSeconds={main?.usedSeconds}
            status={main?.status}
            accentColor={sub.accent}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <PoolCard
            large={large}
            title="Add-on"
            subtitle="Purchased packs"
            remainingSeconds={addon?.remainingSeconds}
            quotaSeconds={addon?.purchasedSeconds ?? addon?.quotaSeconds}
            usedSeconds={addon?.usedSeconds}
            status={addon?.status}
            accentColor="#7c3aed"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <PoolCard
            large={large}
            title="Admin bonus"
            subtitle="Does not reset"
            remainingSeconds={admin?.remainingSeconds}
            quotaSeconds={admin?.grantedSeconds}
            usedSeconds={admin?.usedSeconds}
            status={admin?.status}
            accentColor="#ea580c"
          />
        </Grid>
      </Grid>
      {grants.length > 0 ? (
        <Box
          sx={{
            mt: 2.5,
            p: 2,
            borderRadius: `${theme.radius.md}px`,
            bgcolor: theme.colors.bgSecondary,
            border: `1px solid ${theme.colors.borderDefault}`,
          }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: sub.muted, letterSpacing: '0.06em', mb: 1.25 }}>
            RECENT ADMIN GRANTS
          </Typography>
          {grants.slice(-5).reverse().map((g, i) => (
            <Box
              key={g.grantedAt || i}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                py: 0.85,
                borderTop: i > 0 ? `1px solid ${theme.colors.borderDefault}` : 'none',
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.colors.textPrimary }}>
                {formatHoursLabel(secToHours(g.secondsGranted))}
                {g.preset ? ` (${g.preset})` : ''}
              </Typography>
              {g.grantedAt ? (
                <Typography sx={{ fontSize: 13, color: sub.muted }}>{formatDateShort(g.grantedAt)}</Typography>
              ) : null}
              {g.note ? (
                <Typography sx={{ fontSize: 13, color: sub.muted, flex: '1 1 100%' }}>{g.note}</Typography>
              ) : null}
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

/** Find the catalog entry (from `/client/subscription` availablePlans) matching the active plan. */
function matchCatalogForActive(availablePlansApi, activeRich) {
  if (!Array.isArray(availablePlansApi) || !availablePlansApi.length || !activeRich) return null;
  return (
    availablePlansApi.find(
      (p) =>
        (activeRich.planId && p.id && String(p.id) === String(activeRich.planId)) ||
        (activeRich.planName &&
          p.planName &&
          String(p.planName).toLowerCase() === String(activeRich.planName).toLowerCase()),
    ) || null
  );
}

/** Card shell used across the revamped sections. */
function SectionCard({ title, subtitle, action, children, sx, id }) {
  return (
    <Paper
      id={id}
      elevation={0}
      sx={{
        p: 3,
        ...cardSx,
        height: '100%',
        scrollMarginTop: 96,
        ...sx,
      }}
    >
      {(title || action) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 1.5,
            mb: subtitle ? 0.5 : 2.5,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {title ? (
              <Typography
                component="h2"
                sx={{
                  fontSize: 17,
                  fontWeight: 700,
                  color: theme.colors.textPrimary,
                  letterSpacing: '-0.01em',
                  lineHeight: 1.3,
                }}
              >
                {title}
              </Typography>
            ) : null}
            {subtitle ? (
              <Typography sx={{ fontSize: 13, color: sub.muted, fontWeight: 500, mt: 0.35, lineHeight: 1.45 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {action || null}
        </Box>
      )}
      {children}
    </Paper>
  );
}

/** Stacked horizontal entitlement bar: main + add-on + admin bonus + carry-forward, with used portion overlaid. */
function EntitlementBar({ mainHours, addonHours, adminHours, bonusHours, usedHours }) {
  const total = (mainHours || 0) + (addonHours || 0) + (adminHours || 0) + (bonusHours || 0);
  const safeTotal = total > 0 ? total : 1;
  const seg = (h, color, label) =>
    h > 0 ? (
      <Box
        key={label}
        title={`${label}: ${formatHoursLabel(h)}`}
        sx={{ width: `${(h / safeTotal) * 100}%`, bgcolor: color, height: '100%' }}
      />
    ) : null;
  const usedPct = Math.min(100, Math.round((Math.max(0, usedHours) / safeTotal) * 100));
  const legend = [
    { label: 'Included', h: mainHours, color: sub.accent },
    { label: 'Add-on', h: addonHours, color: '#0d9488' },
    { label: 'Admin bonus', h: adminHours, color: '#ea580c' },
    { label: 'Carry-forward', h: bonusHours, color: '#d97706' },
  ];
  return (
    <Box>
      <Box sx={{ position: 'relative', mb: 1.25 }}>
        <Box sx={{ display: 'flex', height: 14, borderRadius: 999, overflow: 'hidden', bgcolor: sub.barTrack }}>
          {seg(mainHours, sub.accent, 'Included')}
          {seg(addonHours, '#0d9488', 'Add-on')}
          {seg(adminHours, '#ea580c', 'Admin bonus')}
          {seg(bonusHours, '#d97706', 'Carry-forward')}
        </Box>
        <Box
          sx={{
            position: 'absolute',
            top: -3,
            left: `calc(${usedPct}% - 1px)`,
            height: 20,
            width: 2,
            bgcolor: theme.colors.textPrimary,
            borderRadius: 1,
          }}
          title={`Used ${formatHoursLabel(usedHours)} (${usedPct}%)`}
        />
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        {legend
          .filter((x) => (x.h || 0) > 0 || x.label === 'Included')
          .map((x) => (
            <Box key={x.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: x.color }} />
              <Typography sx={{ fontSize: 12, color: sub.muted, fontWeight: 500 }}>
                {x.label} <Box component="span" sx={{ color: theme.colors.textPrimary, fontWeight: 700 }}>{formatHoursLabel(x.h || 0)}</Box>
              </Typography>
            </Box>
          ))}
      </Box>
    </Box>
  );
}

/** Billing / invoice ledger built from paymentHistory[]. */
function BillingLedger({ rows }) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <MdPayments size={36} color={sub.muted} style={{ opacity: 0.45, marginBottom: 12 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: theme.colors.textPrimary, mb: 0.5 }}>
          No payments yet
        </Typography>
        <Typography sx={{ fontSize: 13, color: sub.muted }}>Payments and refunds will appear here once recorded.</Typography>
      </Box>
    );
  }
  const cols = 'minmax(100px, 120px) minmax(140px, 1fr) minmax(100px, 130px) minmax(90px, 110px) minmax(160px, 1.2fr)';
  const cellSx = { fontSize: 13, color: theme.colors.textPrimary, minWidth: 0 };
  const headSx = {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.05em',
    color: sub.muted,
    textTransform: 'uppercase',
    py: 1.25,
  };
  return (
    <Box sx={{ overflowX: 'auto', mx: -0.5 }}>
      <Box sx={{ minWidth: 680 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: cols,
            gap: 2,
            px: 2,
            bgcolor: theme.colors.bgSecondary,
            borderRadius: `${theme.radius.md}px`,
            border: `1px solid ${theme.colors.borderDefault}`,
            mb: 0.5,
          }}
        >
          <Typography sx={headSx}>Date</Typography>
          <Typography sx={headSx}>Plan</Typography>
          <Typography sx={headSx}>Amount</Typography>
          <Typography sx={headSx}>Status</Typography>
          <Typography sx={headSx}>Billing period</Typography>
        </Box>
        {rows.map((p, i) => {
          const refundInfo = getPaymentRefundInfo(p);
          const typeLabel = getPaymentTypeLabel(p.type);
          const methodLabel = formatPaymentMethodLabel(pickPaymentMethodFromRow(p));
          const discounted = p.originalPrice != null && p.discount > 0 && Number(p.originalPrice) !== Number(p.finalPrice);
          const periodStart = p.billing_start || p.trial_start;
          const periodEnd = p.billing_end || p.next_payment_date || p.trial_end;
          let period =
            periodStart || periodEnd
              ? `${periodStart ? formatDateShort(periodStart) : '—'} → ${periodEnd ? formatDateShort(periodEnd) : '—'}`
              : '—';
          const cycleParts = [];
          if (p.billingCycleNumber != null) cycleParts.push(`Cycle ${p.billingCycleNumber}`);
          if (p.paidMonthNumber != null) cycleParts.push(`Month ${p.paidMonthNumber}`);
          if (cycleParts.length) {
            period = period !== '—' ? `${period} · ${cycleParts.join(' · ')}` : cycleParts.join(' · ');
          }
          return (
            <Box
              key={p.id || p.razorpay_payment_id || p.razorpay_order_id || `${p.paymentDate}-${i}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: cols,
                gap: 2,
                px: 2,
                py: 1.5,
                alignItems: 'center',
                borderBottom: i < rows.length - 1 ? `1px solid ${theme.colors.borderDefault}` : 'none',
                bgcolor: i % 2 === 0 ? 'transparent' : theme.colors.bgSecondary,
                borderRadius: i % 2 === 0 ? 0 : `${theme.radius.md}px`,
                transition: 'background-color 0.15s',
                '&:hover': { bgcolor: `${sub.accent}08` },
              }}
            >
              <Typography sx={{ ...cellSx, fontVariantNumeric: 'tabular-nums' }}>
                {formatDateShort(p.paymentDate || p.createdAt)}
              </Typography>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{ ...cellSx, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {p.planName || '—'}
                </Typography>
                {typeLabel ? (
                  <Typography sx={{ fontSize: 11, color: sub.muted, mt: 0.25 }}>{typeLabel}</Typography>
                ) : null}
                {methodLabel ? (
                  <Typography sx={{ fontSize: 11, color: sub.muted, mt: 0.25, fontWeight: 600 }}>
                    {methodLabel}
                  </Typography>
                ) : p.invoice_number ? (
                  <Typography sx={{ fontSize: 11, color: sub.muted, mt: 0.25 }}>{p.invoice_number}</Typography>
                ) : null}
              </Box>
              <Box>
                <Typography sx={{ ...cellSx, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatINR(p.finalPrice)}</Typography>
                {discounted ? (
                  <Typography sx={{ fontSize: 11, color: sub.muted, textDecoration: 'line-through' }}>
                    {formatINR(p.originalPrice)}
                  </Typography>
                ) : null}
              </Box>
              <Box>
                <PaymentStatusChip payment={p} />
                {refundInfo.hasRefund && refundInfo.refundedAt ? (
                  <Typography sx={{ fontSize: 11, color: sub.muted, mt: 0.5 }}>
                    {formatDateShort(refundInfo.refundedAt)}
                    {refundInfo.refundId ? ` · ${refundInfo.refundId}` : ''}
                  </Typography>
                ) : null}
              </Box>
              <Typography sx={{ ...cellSx, color: sub.muted, fontSize: 12, lineHeight: 1.4 }}>{period}</Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

/** Subscription lifecycle timeline: start -> trial -> paid months -> next due -> expiry. */
function LifecycleTimeline({ lifecycle }) {
  if (!lifecycle) {
    return (
      <Typography sx={{ fontSize: 13, color: sub.muted, fontStyle: 'italic', py: 2 }}>
        No active subscription cycle to display.
      </Typography>
    );
  }
  const now = Date.now();
  const upcomingDue = (lifecycle.dueDates || [])
    .map((d) => ({ raw: d, t: new Date(d).getTime() }))
    .filter((x) => !Number.isNaN(x.t))
    .sort((a, b) => a.t - b.t);
  const nextDue = upcomingDue.find((x) => x.t >= now) || upcomingDue[upcomingDue.length - 1] || null;

  const paid = lifecycle.paidMonthsCompleted;
  const commit = lifecycle.autoPayMonths;
  const freeMo = lifecycle.freeMonths ?? lifecycle.freeMonthsGranted;
  const steps = [
    { label: 'Started', date: lifecycle.startDate, done: true },
    {
      label: freeMo ? `Trial / ${freeMo} free mo` : 'Trial',
      date: (lifecycle.trialDates || [])[0] || lifecycle.statusTimestamps?.trial || null,
      done: true,
    },
    {
      label:
        paid != null && commit != null
          ? `Paid ${paid}/${commit} mo`
          : paid != null
            ? `Paid ${paid} mo`
            : 'Billing',
      date: lifecycle.statusTimestamps?.active || null,
      done: paid != null && paid > 0,
    },
    { label: 'Next charge', date: nextDue?.raw || lifecycle.nextPaymentDate, done: false, highlight: true },
    { label: 'Expires', date: lifecycle.planExpiryDate, done: false },
  ].filter((s) => s.date || s.label);

  const usagePeriod =
    lifecycle.usageCycleStart || lifecycle.usageCycleEnd
      ? `${lifecycle.usageCycleStart ? formatDateShort(lifecycle.usageCycleStart) : '—'} → ${lifecycle.usageCycleEnd ? formatDateShort(lifecycle.usageCycleEnd) : '—'}`
      : null;

  return (
    <Box>
      {usagePeriod ? (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.75,
            mb: 2.5,
            borderRadius: `${theme.radius.md}px`,
            bgcolor: theme.colors.bgSecondary,
            border: `1px solid ${theme.colors.borderDefault}`,
          }}
        >
          <MdSchedule size={16} color={sub.accent} />
          <Typography sx={{ fontSize: 13, color: sub.muted }}>
            Current usage period:{' '}
            <Box component="span" sx={{ color: theme.colors.textPrimary, fontWeight: 700 }}>
              {usagePeriod}
            </Box>
          </Typography>
        </Box>
      ) : null}
      <Box
        sx={{
          display: 'flex',
          flexWrap: { xs: 'wrap', lg: 'nowrap' },
          gap: 0,
          alignItems: 'stretch',
          position: 'relative',
        }}
      >
        {steps.map((s, i) => (
          <Box
            key={s.label}
            sx={{
              flex: { xs: '1 1 45%', lg: '1 1 0' },
              minWidth: { xs: 140, lg: 0 },
              position: 'relative',
              pb: 1,
              pr: i < steps.length - 1 ? { lg: 2 } : 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.25 }}>
              <Box
                sx={{
                  width: s.highlight ? 14 : 12,
                  height: s.highlight ? 14 : 12,
                  borderRadius: '50%',
                  flexShrink: 0,
                  bgcolor: s.highlight ? sub.accent : s.done ? sub.success : sub.barTrack,
                  border: s.highlight ? `3px solid ${sub.accentSoft}` : 'none',
                  boxShadow: s.highlight ? `0 0 0 4px ${sub.accent}18` : 'none',
                  zIndex: 1,
                }}
              />
              {i < steps.length - 1 ? (
                <Box
                  sx={{
                    flex: 1,
                    height: 2,
                    ml: 1,
                    bgcolor: s.done ? `${sub.success}55` : sub.barTrack,
                    display: { xs: 'none', lg: 'block' },
                  }}
                />
              ) : null}
            </Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: theme.colors.textPrimary, mb: 0.35 }}>{s.label}</Typography>
            <Typography sx={{ fontSize: 12, color: sub.muted, fontVariantNumeric: 'tabular-nums' }}>
              {s.date ? formatDateShort(s.date) : '—'}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function SubscriptionPage() {
  const { clientEmail, adminView } = useOutletContext();
  // Admin impersonation (`/admin/users/:email/subscription`) loads embedded paymentHistory via ?email=.
  void adminView;
  const [data, setData] = React.useState(null);
  const [overviewData, setOverviewData] = React.useState(null);
  const [planUsage, setPlanUsage] = React.useState(null);
  const [usageForecastInsight, setUsageForecastInsight] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const refetchUsageForecast = React.useCallback(async () => {
    const email = clientEmail || getClientEmail();
    if (!email) return;
    try {
      const subRes = await api.get(`/client/subscription?email=${encodeURIComponent(email)}`);
      if (subRes.data?.success) {
        const fromSub =
          normalizeUsageForecastInsight({ data: subRes.data?.usageForecast }) ||
          normalizeUsageForecastInsight({ data: subRes.data?.usageForecastCache }) ||
          normalizeUsageForecastInsight({ data: subRes.data?.subscriptionInsights }) ||
          normalizeUsageForecastInsight({ data: subRes.data?.insightsFromView });
        setUsageForecastInsight(fromSub || null);
      }
    } catch (e) {
      console.error('Usage forecast refetch error:', e);
    }
  }, [clientEmail]);

  React.useEffect(() => {
    const load = async () => {
      const email = clientEmail || getClientEmail();
      if (!email) {
        setLoading(false);
        return;
      }
      try {
        const [subRes, ovRes, puRes, insightRes] = await Promise.all([
          api.get(`/client/subscription?email=${encodeURIComponent(email)}`).catch(() => ({ data: {} })),
          api.get(`/client/overview?email=${encodeURIComponent(email)}`).catch(() => ({ data: {} })),
          api.get('/user/plan-usage', { params: { email } }).catch(() => ({ data: null })),
          api.get('/client/insights', { params: { email, surface: 'subscription' } }).catch(() => ({ data: null })),
        ]);
        if (subRes.data?.success) setData(subRes.data);
        if (ovRes.data?.success) setOverviewData(ovRes.data);
        const puBody = normalizePlanUsageBody(puRes);
        if (puBody) setPlanUsage(puBody);

        const fromDedicated = normalizeUsageForecastInsight(insightRes);
        const fromSub =
          normalizeUsageForecastInsight({ data: subRes.data?.usageForecast }) ||
          normalizeUsageForecastInsight({ data: subRes.data?.usageForecastCache }) ||
          normalizeUsageForecastInsight({ data: subRes.data?.subscriptionInsights }) ||
          normalizeUsageForecastInsight({ data: subRes.data?.insightsFromView });
        setUsageForecastInsight(fromDedicated || fromSub || null);
      } catch (e) {
        console.error('Subscription / overview / plan-usage fetch error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [clientEmail]);

  const merged = useMemo(() => {
    const plan = data?.plan || {};
    const usd = data?.userSubscriptionDetails ?? {};
    const billing = data?.billingSummary || {};
    const paymentHistory = usd.paymentHistory || [];
    const latest = pickLatestPayment(paymentHistory);
    const fromPaymentTier = parseTierFromPlanName(latest?.planName);
    const displayTier =
      plan.monthlyFee != null && plan.renewalDate && plan.renewalDate !== '—'
        ? plan.tier || fromPaymentTier
        : fromPaymentTier || plan.tier || 'Silver';

    const monthlyFee =
      plan.monthlyFee != null ? plan.monthlyFee : latest?.finalPrice ?? latest?.amount ?? null;

    const trimDate = trimRenewalCandidate;

    /** Primary renewal / next-charge instant from subscription + payment rows (plan-usage merged later). */
    let renewalIso =
      trimDate(plan.renewalDate) ||
      trimDate(plan.nextSubscriptionDateISO) ||
      trimDate(plan.nextSubscriptionDate) ||
      trimDate(latest?.next_payment_date) ||
      trimDate(latest?.billing_end) ||
      trimDate(plan.nextBilling) ||
      trimDate(plan.nextBillingDate) ||
      trimDate(plan.next_billing_date) ||
      trimDate(plan.nextRenewal) ||
      trimDate(plan.currentPeriodEnd) ||
      trimDate(plan.current_period_end) ||
      trimDate(plan.billingPeriodEnd) ||
      trimDate(plan.billing_period_end) ||
      trimDate(data?.nextRenewal) ||
      trimDate(usd.nextRenewal) ||
      trimDate(usd.nextBilling) ||
      trimDate(usd.next_billing_date) ||
      trimDate(usd.nextBillingDate) ||
      trimDate(usd.trialEndDate) ||
      trimDate(usd.trial_ends_at) ||
      trimDate(usd.trialEndsAt) ||
      trimDate(usd.subscriptionEndDate) ||
      trimDate(usd.subscription_end_date) ||
      trimDate(usd.currentPeriodEnd) ||
      trimDate(usd.current_period_end) ||
      trimDate(usd.nextSubscriptionDateISO) ||
      trimDate(usd.nextSubscriptionDate) ||
      trimDate(extractBillingFromPlanUsageState(planUsage)) ||
      trimDate(billing.nextChargeDate) ||
      null;

    const puPick = pickPlanUsage(planUsage);
    const usePlanUsage = Boolean(
      puPick &&
        [
          puPick.planLabel,
          puPick.includedHours,
          puPick.usedHours,
          puPick.remainingHours,
          puPick.monthlyFee,
          puPick.nextBilling,
          puPick.usagePct,
          puPick.usedTimeFormatted,
          puPick.remainingTimeFormatted,
          puPick.planpaymentStatus,
          puPick.pools,
          puPick.adminGrantedSeconds,
        ].some((v) => v != null && v !== ''),
    );
    const pu = puPick || {};

    /** `/user/plan-usage` often carries `nextBilling` when `/client/subscription` omits `plan.renewalDate` (e.g. trials, add-ons). */
    if (usePlanUsage && trimDate(pu.nextBilling) && (!renewalIso || !isRenewalDateParsable(renewalIso))) {
      renewalIso = trimDate(pu.nextBilling);
    }

    /** Single `Date` for labels + day count (handles DD/MM/YYYY, Unix, etc.). */
    const renewalDateResolved =
      parseRenewalDateFlexible(renewalIso) ||
      parseRenewalDateFlexible(trimDate(plan.nextSubscriptionDateISO)) ||
      parseRenewalDateFlexible(trimDate(plan.nextSubscriptionDate)) ||
      parseRenewalDateFlexible(trimDate(extractBillingFromPlanUsageState(planUsage))) ||
      (usePlanUsage ? parseRenewalDateFlexible(trimDate(pu.nextBilling)) : null) ||
      parseRenewalDateFlexible(trimDate(plan.renewalDate)) ||
      null;

    let daysToRenewal =
      plan.daysToRenewal != null && plan.daysToRenewal !== '' && Number.isFinite(Number(plan.daysToRenewal))
        ? Number(plan.daysToRenewal)
        : plan.daysToNextSubscription != null &&
            plan.daysToNextSubscription !== '' &&
            Number.isFinite(Number(plan.daysToNextSubscription))
          ? Number(plan.daysToNextSubscription)
          : null;
    if (daysToRenewal == null) {
      daysToRenewal = renewalDateResolved
        ? daysFromNowTo(renewalDateResolved)
        : renewalIso
          ? daysFromNowTo(renewalIso)
          : null;
    }

    const renewalLabel =
      renewalDateResolved != null
        ? renewalDateResolved.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        : formatDateShort(renewalIso) || trimDate(plan.renewalDate) || '—';

    const renewalBannerDateLabel =
      renewalDateResolved != null
        ? formatRenewalBannerDate(renewalDateResolved)
        : renewalLabel !== '—'
          ? renewalLabel
          : '—';

    const renewalPlanNameForBanner = buildRenewalBannerPlanPhrase(displayTier, latest, plan, usePlanUsage, pu);

    const currentPlanTitle =
      usePlanUsage && pu.planLabel
        ? pu.planLabel.trim().toLowerCase().endsWith('tier')
          ? pu.planLabel.trim()
          : `${pu.planLabel.trim()} tier`
        : `${displayTier} tier`;

    const monthlyFeeDisplay = usePlanUsage && pu.monthlyFee != null ? pu.monthlyFee : monthlyFee;

    const tierCeiling =
      usePlanUsage && pu.includedHours != null && pu.includedHours > 0 ? pu.includedHours : tierHours(displayTier);

    const hoursUsed =
      usePlanUsage && pu.usedHours != null
        ? Math.max(0, Number(pu.usedHours))
        : plan.hoursUsed != null
          ? Number(plan.hoursUsed)
          : 0;

    const remainingH =
      usePlanUsage && pu.remainingHours != null
        ? Math.max(0, Number(pu.remainingHours))
        : Math.max(0, tierCeiling - hoursUsed);

    const usagePct =
      usePlanUsage && pu.usagePct != null
        ? Math.round(Math.min(100, Math.max(0, Number(pu.usagePct))))
        : usePlanUsage && pu.totalCombinedQuotaSeconds > 0
          ? Math.round(Math.min(100, ((hoursUsed * 3600) / pu.totalCombinedQuotaSeconds) * 100))
          : tierCeiling > 0
            ? Math.round(Math.min(100, (hoursUsed / tierCeiling) * 100))
            : 0;

    const planCardNextBilling =
      usePlanUsage && pu.nextBilling != null && String(pu.nextBilling).trim() && String(pu.nextBilling) !== '—'
        ? formatDateShort(pu.nextBilling)
        : renewalDateResolved != null
          ? renewalDateResolved.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
          : billing.nextChargeDate
            ? formatDateShort(billing.nextChargeDate)
            : renewalLabel;

    const usedHoursDisplay = usePlanUsage && pu.usedTimeFormatted ? pu.usedTimeFormatted : `${hoursUsed.toFixed(1)} h`;
    const remainingHoursDisplay =
      usePlanUsage && pu.remainingTimeFormatted ? pu.remainingTimeFormatted : `${remainingH.toFixed(1)} h`;

    const usageRaw = Array.isArray(data?.usageChart) ? [...data.usageChart] : [];
    const usageActual = usageRaw.map((row) => ({
      month: row.month ?? row.label ?? '—',
      hours: Number(row.hours ?? row.h ?? 0) || 0,
    }));
    const hasRealUsage = usageActual.length > 0 && usageActual.some((r) => r.hours > 0);
    // Show real data only; never fall back to fabricated placeholder bars.
    const usageChartIsPlaceholder = !hasRealUsage;
    const usageChart = usageActual;

    const hoursListForStats = usageActual.map((r) => r.hours);
    const avgH = hoursListForStats.length ? hoursListForStats.reduce((a, b) => a + b, 0) / hoursListForStats.length : 0;
    let peakH = 0;
    let peakMonth = '';
    usageActual.forEach((r) => {
      if (r.hours > peakH) {
        peakH = r.hours;
        peakMonth = r.month;
      }
    });

    const lifetime = data?.lifetimeSummary || data?.lifetime || {};
    const ov = overviewData || {};
    const k = ov.kpis || {};
    const hoursChart = Array.isArray(ov.hoursChart) ? ov.hoursChart : [];
    const hoursFromChart = hoursChart.reduce((sum, row) => sum + (Number(row.hours ?? row.Hours ?? 0) || 0), 0);
    const hoursFromSubscriptionChart = usageActual.reduce((sum, row) => sum + (Number(row.hours) || 0), 0);

    const lifetimeHoursVal =
      firstFiniteNumber(
        lifetime.lifetimeHours,
        lifetime.lifetime_uvc_hours,
        lifetime.totalUvcHours,
        data?.lifetimeHours,
        data?.totalUvcHours,
        k.totalUvcHours,
        k.lifetimeUvcHours,
        k.lifetimeHours,
        k.hoursTotal,
        k.hoursAllTime,
        k.hoursLifetime,
      ) ??
      (hoursFromSubscriptionChart > 0 ? hoursFromSubscriptionChart : null) ??
      (hoursFromChart > 0 ? hoursFromChart : null);

    const totalSessionsVal = firstFiniteNumber(
      data?.lifetimeSessions,
      data?.userTotalSessions,
      usd.lifetimeSessions,
      usd.userTotalSessions,
      usd.totalSessions,
      plan.lifetimeSessions,
      plan.userTotalSessions,
      plan.totalSessions,
      lifetime.lifetimeSessions,
      lifetime.totalSessions,
      lifetime.total_sessions,
      data?.totalSessions,
      k.userTotalSessions,
      k.totalSessions,
      k.lifetimeSessions,
      k.sessionCount,
      k.sessionsTotal,
      k.sessions,
    );

    const roomCountVal = firstFiniteNumber(lifetime.roomCount, lifetime.rooms_covered, k.roomsCovered, k.rooms, k.uniqueRooms);

    const earliestPaid = earliestPaymentMonthLabel(paymentHistory);
    const lifetimeHoursSub =
      lifetime.sinceLabel ||
      lifetime.since ||
      (earliestPaid ? `since ${earliestPaid}` : null) ||
      (hoursFromSubscriptionChart > 0 ? 'Total from subscription usage chart' : null) ||
      (hoursFromChart > 0 ? 'Total from UVC hours chart (overview)' : null) ||
      '—';

    const totalSessionsSub =
      lifetime.roomsLabel ||
      (roomCountVal != null && roomCountVal >= 0 ? `across ${roomCountVal} room${roomCountVal === 1 ? '' : 's'}` : null) ||
      (totalSessionsVal != null && k.sessions != null && Number(totalSessionsVal) === Number(k.sessions) ? 'All time · dashboard' : null) ||
      'All time · this account';

    const sessionChartRaw = Array.isArray(data?.sessionsChart)
      ? data.sessionsChart
      : Array.isArray(ov.sessionsChart)
        ? ov.sessionsChart
        : [];

    const refNow = new Date();
    const currentMonthCaption = refNow.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

    let currentMonthHoursVal = firstFiniteNumber(
      data?.currentMonthHours,
      data?.hoursThisMonth,
      data?.monthHours,
      lifetime.currentMonthHours,
      lifetime.hoursThisMonth,
      k.currentMonthHours,
      k.hoursThisMonth,
      k.monthHours,
      k.monthlyUvcHours,
    );
    if (currentMonthHoursVal == null) {
      currentMonthHoursVal = sumHoursForCalendarMonth(usageActual, refNow);
    }
    if (currentMonthHoursVal == null) {
      currentMonthHoursVal = sumHoursForCalendarMonth(
        hoursChart.map((row) => ({
          month: row.month ?? row.label ?? row.period,
          hours: row.hours ?? row.Hours,
        })),
        refNow,
      );
    }
    if (currentMonthHoursVal == null && usePlanUsage && hoursUsed != null) {
      currentMonthHoursVal = Number(hoursUsed) || 0;
    }

    let currentMonthSessionsVal = firstFiniteNumber(
      data?.currentMonthSessions,
      plan.currentMonthSessions,
      plan.sessionsThisMonth,
      plan.monthSessions,
      usd.currentMonthSessions,
      usd.sessionsThisMonth,
      usd.monthSessions,
      data?.sessionsThisMonth,
      lifetime.currentMonthSessions,
      k.currentMonthSessions,
      k.sessionsThisMonth,
      k.monthSessions,
      k.sessionsThisMonthCount,
    );
    if (currentMonthSessionsVal == null) {
      currentMonthSessionsVal = sumSessionsForCalendarMonth(sessionChartRaw, refNow);
    }

    const currentMonthHoursSub =
      usePlanUsage &&
      hoursUsed != null &&
      currentMonthHoursVal != null &&
      sumHoursForCalendarMonth(usageActual, refNow) == null &&
      sumHoursForCalendarMonth(
        hoursChart.map((row) => ({
          month: row.month ?? row.label ?? row.period,
          hours: row.hours ?? row.Hours,
        })),
        refNow,
      ) == null &&
      Number(currentMonthHoursVal) === Number(hoursUsed)
        ? `${currentMonthCaption} · from active plan usage`
        : currentMonthCaption;

    const currentMonthSessionsSub = currentMonthCaption;

    // ---- Rich subscription detail (serialized plans + billing summary + ledger) ----
    const richPlans = Array.isArray(usd.plans) ? usd.plans : [];
    const activeRich =
      richPlans.find((p) => p.planpaymentStatus === 'active' || p.planpaymentStatus === 'trial') ||
      usd.activeSubscription ||
      richPlans[0] ||
      null;
    const availablePlansApi = Array.isArray(data?.availablePlans) ? data.availablePlans : [];
    const matchedCatalog = matchCatalogForActive(availablePlansApi, activeRich);

    const paymentRows = [...paymentHistory].sort((a, b) => {
      const da = new Date(a.paymentDate || a.createdAt || 0).getTime();
      const db = new Date(b.paymentDate || b.createdAt || 0).getTime();
      return db - da;
    });

    const mandateRefundDetected = paymentRows.some((p) => getPaymentRefundInfo(p).isMandateAuthRefund);

    const puPools = pu.pools || planUsage?.pools || null;

    // Entitlement breakdown: main + add-on + admin bonus + carry-forward.
    const mainHours =
      puPools?.main?.quotaSeconds != null
        ? secToHours(puPools.main.quotaSeconds)
        : firstFiniteNumber(activeRich?.mainHoursMonthly, activeRich?.totalHoursPerMonth) ?? tierCeiling;
    const addonHours =
      puPools?.addon?.purchasedSeconds != null
        ? secToHours(puPools.addon.purchasedSeconds)
        : secToHours(activeRich?.addonPurchasedSeconds);
    const adminHours =
      puPools?.admin?.grantedSeconds != null
        ? secToHours(puPools.admin.grantedSeconds)
        : secToHours(activeRich?.adminGrantedSeconds) || secToHours(pu.adminGrantedSeconds);
    const bonusHours = firstFiniteNumber(activeRich?.cycleEndBonusHours) ?? 0;
    const totalEntitlement = (Number(mainHours) || 0) + addonHours + adminHours + bonusHours;
    const entitlementUsed = hoursUsed;
    const entitlementRemaining =
      puPools?.totalRemainingSeconds != null
        ? secToHours(puPools.totalRemainingSeconds)
        : usePlanUsage && pu.remainingHours != null
          ? Number(pu.remainingHours)
          : activeRich?.remainingSeconds != null
            ? secToHours(activeRich.remainingSeconds)
            : Math.max(0, totalEntitlement - entitlementUsed);

    const timePools = puPools
      ? {
          main: {
            quotaSeconds: puPools.main?.quotaSeconds ?? (Number(mainHours) || 0) * 3600,
            usedSeconds: puPools.main?.usedSeconds ?? 0,
            remainingSeconds:
              puPools.main?.remainingSeconds ??
              activeRich?.mainRemainingSeconds ??
              Math.max(0, (Number(mainHours) || 0) * 3600 - (puPools.main?.usedSeconds || 0)),
            status: puPools.main?.status || pu.poolStatuses?.main || (mainHours > 0 ? 'active' : 'inactive'),
          },
          addon: {
            purchasedSeconds: puPools.addon?.purchasedSeconds ?? activeRich?.addonPurchasedSeconds ?? 0,
            usedSeconds: puPools.addon?.usedSeconds ?? 0,
            remainingSeconds: puPools.addon?.remainingSeconds ?? activeRich?.addonRemainingSeconds ?? 0,
            status: puPools.addon?.status || pu.poolStatuses?.addon || (addonHours > 0 ? 'active' : 'inactive'),
          },
          admin: {
            grantedSeconds: puPools.admin?.grantedSeconds ?? activeRich?.adminGrantedSeconds ?? 0,
            usedSeconds: puPools.admin?.usedSeconds ?? 0,
            remainingSeconds: puPools.admin?.remainingSeconds ?? activeRich?.adminRemainingSeconds ?? 0,
            status: puPools.admin?.status || pu.poolStatuses?.admin || (adminHours > 0 ? 'active' : 'inactive'),
          },
        }
      : activeRich
        ? {
            main: {
              quotaSeconds: (Number(mainHours) || 0) * 3600,
              usedSeconds: Math.max(0, (Number(mainHours) || 0) * 3600 - (Number(activeRich.mainRemainingSeconds) || 0)),
              remainingSeconds: activeRich.mainRemainingSeconds ?? (Number(mainHours) || 0) * 3600,
              status: mainHours > 0 ? 'active' : 'inactive',
            },
            addon: {
              purchasedSeconds: activeRich.addonPurchasedSeconds ?? 0,
              usedSeconds: Math.max(0, (Number(activeRich.addonPurchasedSeconds) || 0) - (Number(activeRich.addonRemainingSeconds) || 0)),
              remainingSeconds: activeRich.addonRemainingSeconds ?? 0,
              status: addonHours > 0 ? 'active' : 'inactive',
            },
            admin: {
              grantedSeconds: activeRich.adminGrantedSeconds ?? 0,
              usedSeconds: Math.max(0, (Number(activeRich.adminGrantedSeconds) || 0) - (Number(activeRich.adminRemainingSeconds) || 0)),
              remainingSeconds: activeRich.adminRemainingSeconds ?? 0,
              status: adminHours > 0 ? 'active' : 'inactive',
            },
          }
        : null;

    const sessionsAllowance = firstFiniteNumber(
      matchedCatalog?.benefits?.sessionsPerMonth,
      matchedCatalog?.benefits?.sessions_per_month,
    );
    const currentMonthSessions = firstFiniteNumber(data?.currentMonthSessions) ?? null;
    const lifetimeSessions = firstFiniteNumber(data?.lifetimeSessions) ?? null;

    const heroStatus =
      activeRich?.subscriptionStatus ||
      activeRich?.planpaymentStatus ||
      (plan.status && plan.status !== '—' ? plan.status : null) ||
      null;
    const memberSince = trimDate(billing.firstPaymentDate);

    const lifecycle = activeRich
      ? {
          startDate: activeRich.startDate || activeRich.planPurchaseDate || null,
          trialDates: Array.isArray(activeRich.trialDates) ? activeRich.trialDates : [],
          dueDates: Array.isArray(activeRich.dueDates) ? activeRich.dueDates : [],
          statusTimestamps: activeRich.paymentStatusTimestamps || {},
          paidMonthsCompleted: activeRich.paidMonthsCompleted ?? null,
          freeMonths: activeRich.freeMonths ?? activeRich.freeMonthsGranted ?? null,
          freeMonthsGranted: activeRich.freeMonthsGranted ?? activeRich.freeMonths ?? null,
          autoPayMonths: activeRich.autoPayMonths ?? null,
          planExpiryDate: activeRich.planExpiryDate || null,
          nextPaymentDate: activeRich.nextPaymentDate || billing.nextChargeDate || null,
          usageCycleStart: activeRich.usageCycleStart || null,
          usageCycleEnd: activeRich.usageCycleEnd || null,
          planName: activeRich.planName || displayTier,
        }
      : null;

    const paymentDisplay = resolveSubscriptionPaymentDisplay(usd, paymentRows, activeRich, billing);

    return {
      plan,
      displayTier,
      monthlyFee,
      monthlyFeeDisplay,
      richPlans,
      activeRich,
      billing,
      paymentRows,
      mainHours: Number(mainHours) || 0,
      addonHours,
      adminHours,
      bonusHours,
      totalEntitlement,
      entitlementUsed,
      entitlementRemaining,
      timePools,
      mandateRefundDetected,
      adminTimeGrants: activeRich?.adminTimeGrants || [],
      sessionsAllowance,
      currentMonthSessions,
      lifetimeSessions,
      heroStatus,
      memberSince,
      lifecycle,
      paymentDisplay,
      renewalLabel,
      renewalBannerDateLabel,
      renewalPlanNameForBanner,
      planCardNextBilling,
      currentPlanTitle,
      usedHoursDisplay,
      remainingHoursDisplay,
      planPaymentStatus: usePlanUsage ? pu.planpaymentStatus : null,
      daysToRenewal,
      tierCeiling,
      hoursUsed,
      usagePct,
      remainingH,
      usageChart,
      usageChartIsPlaceholder,
      avgH,
      peakH,
      peakMonth,
      lifetimeHoursVal,
      lifetimeHoursSub,
      totalSessionsVal,
      totalSessionsSub,
      currentMonthHoursVal,
      currentMonthHoursSub,
      currentMonthSessionsVal,
      currentMonthSessionsSub,
      chartCeiling: totalEntitlement > 0 ? totalEntitlement : tierCeiling,
    };
  }, [data, overviewData, planUsage]);

  const showRenewalBanner = useMemo(
    () =>
      merged.daysToRenewal != null &&
      merged.daysToRenewal >= 0 &&
      merged.daysToRenewal <= RENEWAL_REMINDER_WINDOW_DAYS &&
      merged.renewalBannerDateLabel != null &&
      merged.renewalBannerDateLabel !== '—',
    [merged.daysToRenewal, merged.renewalBannerDateLabel],
  );

  const usageForecastSource = React.useMemo(() => {
    const fc = usageForecastInsight;
    const hasPoints = Array.isArray(fc?.points) && fc.points.length > 0;
    const hasNarrative = typeof fc?.narrative === 'string' && fc.narrative.trim().length > 0;
    if (fc?.source === 'ai' || fc?.source === 'template') return fc.source;
    return hasPoints || hasNarrative ? 'ai' : 'template';
  }, [usageForecastInsight]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 360 }}>
        <CircularProgress sx={{ color: theme.colors.brandPrimary }} />
      </Box>
    );
  }

  const purchaseTypeNorm = (data?.userSubscriptionDetails?.purchaseType ?? '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
  if (data?.success && purchaseTypeNorm === 'purchaseuser') {
    return null;
  }

  const {
    monthlyFeeDisplay,
    renewalBannerDateLabel,
    renewalPlanNameForBanner,
    planCardNextBilling,
    currentPlanTitle,
    usedHoursDisplay,
    remainingHoursDisplay,
    daysToRenewal,
    usagePct,
    usageChart,
    avgH,
    peakH,
    peakMonth,
    usageChartIsPlaceholder,
    lifetimeHoursVal,
    lifetimeHoursSub,
    totalSessionsVal,
    totalSessionsSub,
    currentMonthHoursVal,
    currentMonthHoursSub,
    currentMonthSessionsVal,
    currentMonthSessionsSub,
    billing,
    paymentRows,
    mainHours,
    addonHours,
    adminHours,
    bonusHours,
    totalEntitlement,
    entitlementUsed,
    entitlementRemaining,
    timePools,
    adminTimeGrants,
    mandateRefundDetected,
    sessionsAllowance,
    currentMonthSessions,
    heroStatus,
    memberSince,
    lifecycle,
    paymentDisplay,
    chartCeiling,
  } = merged;

  const chartYMax = Math.max(40, Math.ceil(chartCeiling / 10) * 10);
  const subEmail = clientEmail || getClientEmail() || '';
  const usageChartHeight = usageChartIsPlaceholder
    ? 200
    : Math.min(240, Math.max(168, usageChart.length * 52 + 64));

  const usageStatRows = [
    { label: 'Used', value: usedHoursDisplay },
    {
      label: 'Remaining',
      value: entitlementRemaining != null ? formatHoursLabel(entitlementRemaining) : remainingHoursDisplay,
    },
    ...(sessionsAllowance != null
      ? [
          {
            label: 'Sessions this month',
            value: `${currentMonthSessions != null ? Math.round(currentMonthSessions) : 0} / ${Math.round(sessionsAllowance)}`,
          },
        ]
      : []),
    { label: 'Next billing', value: planCardNextBilling },
  ];

  return (
    <Box component="article">
      <PageHeader
        title="Subscription"
        subtitle="Plan details, usage against your allowance, billing history, and renewal timeline."
      />

      <SubscriptionPaymentBanner />

      {showRenewalBanner ? (
        <RenewalBanner
          planName={renewalPlanNameForBanner}
          dateLabel={renewalBannerDateLabel}
          daysAway={daysToRenewal}
        />
      ) : null}

      {/* Current plan summary */}
      <Paper
        elevation={0}
        sx={{
          ...cardSx,
          mb: 3,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            height: 4,
            background: theme.gradients.primary,
          }}
        />
        <Box sx={{ p: { xs: 2.5, md: 3 } }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              mb: 3,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="h2"
                sx={{
                  fontSize: { xs: 22, md: 26 },
                  fontWeight: 800,
                  color: theme.colors.textPrimary,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                }}
              >
                {currentPlanTitle}
              </Typography>
              <Typography sx={{ fontSize: 14, color: sub.muted, mt: 0.5, fontWeight: 500 }}>
                Active subscription · UV disinfection plan
                {memberSince ? ` · Client since ${formatDateShort(memberSince)}` : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
              {paymentDisplay?.label && paymentDisplay.label !== '—' ? (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 999,
                    bgcolor: `${sub.accent}12`,
                    color: sub.accentText,
                    fontSize: 12,
                    fontWeight: 700,
                    border: `1px solid ${sub.accentBorder}`,
                  }}
                >
                  <MdCreditCard size={14} />
                  {paymentDisplay.label}
                </Box>
              ) : null}
              {heroStatus ? <StatusChip status={heroStatus} size="md" /> : null}
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricTile
                icon={<MdCreditCard size={18} />}
                label="Monthly fee"
                value={monthlyFeeDisplay != null ? formatINR(monthlyFeeDisplay) : '—'}
                subtext="Recurring · per month"
                accent={theme.colors.brandPrimary}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricTile
                icon={<MdPayments size={18} />}
                label="Lifetime value"
                value={billing?.lifetimeValue != null ? formatINR(billing.lifetimeValue) : '—'}
                subtext={
                  billing?.totalRefunded > 0
                    ? `${formatINR(billing.totalRefunded)} refunded${(billing?.refundedCount ?? 0) > 0 ? ` · ${billing.refundedCount} refund${billing.refundedCount === 1 ? '' : 's'}` : ''}`
                    : `${billing?.capturedCount ?? 0} captured payment${(billing?.capturedCount ?? 0) === 1 ? '' : 's'}`
                }
                accent="#7c3aed"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricTile
                icon={<MdAutorenew size={18} />}
                label="Next charge"
                value={planCardNextBilling || '—'}
                subtext={
                  daysToRenewal != null && daysToRenewal >= 0
                    ? `${daysToRenewal} day${daysToRenewal === 1 ? '' : 's'} away`
                    : billing?.nextChargeAmount != null
                      ? formatINR(billing.nextChargeAmount)
                      : 'Scheduled renewal'
                }
                accent={sub.accent}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricTile
                icon={<MdCreditCard size={18} />}
                label="Payment method"
                value={paymentDisplay?.label || '—'}
                subtext={paymentDisplay?.subtext || 'Payment method on file'}
                accent={paymentDisplay?.isAutopay ? sub.success : '#0d9488'}
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Usage & chart — equal columns, compact height */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }} alignItems="stretch">
        <Grid item xs={12} lg={6}>
          <SectionCard
            title="Entitlement & usage"
            subtitle={
              totalEntitlement > 0 ? `${formatHoursLabel(totalEntitlement)} total this billing cycle` : 'Allowance not set'
            }
          >
            <Box sx={{ py: 0.5 }}>
              <SemicircleGauge pct={usagePct} />
            </Box>

            <Box sx={{ mt: 0.5, mb: 1.5 }}>
              <EntitlementBar
                mainHours={mainHours}
                addonHours={addonHours}
                adminHours={adminHours}
                bonusHours={bonusHours}
                usedHours={entitlementUsed}
              />
            </Box>

            <Box
              sx={{
                px: 0.5,
                py: 0.5,
                borderRadius: `${theme.radius.md}px`,
                bgcolor: theme.colors.bgSecondary,
                border: `1px solid ${theme.colors.borderDefault}`,
              }}
            >
              {usageStatRows.map((row, idx) => (
                <Box key={row.label} sx={{ px: 1.5 }}>
                  <UsageStatRow label={row.label} value={row.value} isLast={idx === usageStatRows.length - 1} />
                </Box>
              ))}
            </Box>
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <SectionCard
            title="Monthly UV usage vs plan ceiling"
            subtitle={`Avg ${avgH.toFixed(1)} h · peak ${peakH.toFixed(1)} h${peakMonth && peakH > 0 ? ` (${peakMonth})` : ''}`}
          >
            {usageChartIsPlaceholder ? (
              <Box
                sx={{
                  minHeight: usageChartHeight,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  gap: 1.25,
                  px: 2,
                  py: 3,
                  borderRadius: `${theme.radius.md}px`,
                  bgcolor: theme.colors.bgSecondary,
                  border: `1px dashed ${theme.colors.borderDefault}`,
                }}
              >
                <MdTrendingUp size={36} color={sub.muted} style={{ opacity: 0.4 }} />
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: theme.colors.textPrimary }}>
                  No disinfection usage recorded yet
                </Typography>
                <Typography sx={{ fontSize: 13, color: sub.muted, maxWidth: 320, lineHeight: 1.5 }}>
                  Monthly UV hours will appear here once robots start logging sessions.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ height: usageChartHeight, mt: 0.25 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={usageChart} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.colors.borderDefault} />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 12, fill: sub.muted }}
                      axisLine={{ stroke: theme.colors.borderDefault }}
                      tickLine={false}
                    />
                    <YAxis
                      domain={[0, chartYMax]}
                      ticks={Array.from({ length: chartYMax / 10 + 1 }, (_, i) => i * 10)}
                      tickFormatter={(v) => `${v}h`}
                      tick={{ fontSize: 12, fill: sub.muted }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip cursor={{ fill: `${sub.accent}08` }} content={<UsageChartTooltip />} />
                    <ReferenceLine
                      y={chartCeiling}
                      stroke={sub.ceiling}
                      strokeDasharray="6 4"
                      strokeWidth={2}
                      label={{
                        value: 'Plan ceiling',
                        position: 'insideTopRight',
                        fill: sub.ceiling,
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    />
                    <Bar dataKey="hours" fill={sub.accent} radius={[8, 8, 0, 0]} name="Hours" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}

            <DashboardInsightCard
              title="Usage forecast"
              insightsFromView={usageForecastInsight}
              source={usageForecastSource}
              loading={loading}
              disclaimerMode="short"
              attributionLive="Generated by AI from your current subscription and usage data."
              attributionCached="Generated by AI from your subscription and usage data."
              emptyBodyText="Updating with AI"
              adminCacheInvalidation={{
                surface: 'subscription',
                cacheParams: {
                  email: subEmail,
                  calcMonth: currentYYYYMM(),
                },
                onInvalidated: refetchUsageForecast,
              }}
              sx={{ mt: 1.5, mb: 0 }}
            />
          </SectionCard>
        </Grid>
      </Grid>

      {/* Time pools — full width so cards are not squeezed */}
      {hasVisibleTimePools(timePools) ? (
        <Box sx={{ mb: 2.5 }}>
          <SectionCard
            title="Time pools"
            subtitle="Main hours reset each billing month. Add-on and admin bonus persist until used (consumed after main time)."
          >
            <TimePoolsSection timePools={timePools} adminTimeGrants={adminTimeGrants} large />
          </SectionCard>
        </Box>
      ) : null}

      {/* Activity KPIs */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiStatCard
            icon={<MdAccessTime size={20} />}
            label="Lifetime hours"
            value={lifetimeHoursVal != null ? `${Number(lifetimeHoursVal).toFixed(1)} h` : '—'}
            subtext={lifetimeHoursSub || '—'}
            accent={sub.accent}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiStatCard
            icon={<MdEvent size={20} />}
            label="Total sessions"
            value={totalSessionsVal != null ? String(Math.round(totalSessionsVal)) : '—'}
            subtext={totalSessionsSub || '—'}
            accent={theme.colors.brandPrimary}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiStatCard
            icon={<MdCalendarToday size={20} />}
            label="Current month hours"
            value={currentMonthHoursVal != null ? `${Number(currentMonthHoursVal).toFixed(1)} h` : '—'}
            subtext={currentMonthHoursSub || '—'}
            accent="#7c3aed"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiStatCard
            icon={<MdInsights size={20} />}
            label="Current month sessions"
            value={currentMonthSessionsVal != null ? String(Math.round(currentMonthSessionsVal)) : '—'}
            subtext={currentMonthSessionsSub || '—'}
            accent={sub.success}
          />
        </Grid>
      </Grid>

      {lifecycle ? (
        <Box sx={{ mb: 3 }}>
          <SectionCard
            title="Subscription lifecycle"
            subtitle="Trial through paid commitment, next charge and expiry"
          >
            <LifecycleTimeline lifecycle={lifecycle} />
          </SectionCard>
        </Box>
      ) : null}

      {mandateRefundDetected ? (
        <Alert severity="info" sx={{ mb: 2.5, borderRadius: `${theme.radius.md}px` }}>
          Your autopay verification charge was refunded. Month 1 remains free; plan billing starts from month 2.
        </Alert>
      ) : null}

      <SectionCard
        title="Billing history"
        subtitle="Every payment, refund and authorisation on this account"
        action={
          paymentRows?.length ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: sub.muted }}>
              <MdHistory size={16} />
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{paymentRows.length} records</Typography>
            </Box>
          ) : null
        }
      >
        <BillingLedger rows={paymentRows} />
      </SectionCard>
    </Box>
  );
}
